import { ReactNode, useEffect, useId, useRef } from 'react'

import { Button } from 'components/atoms/button'

/**
 * These panels replace the focused code field, so focus would otherwise drop to the page and nothing would say why
 * sign-in ended. The heading takes focus on arrival and is described by the explanation under it.
 */
function useFocusOnArrival() {
	const headingRef = useRef<HTMLHeadingElement>(null)
	const descriptionId = useId()
	useEffect(() => {
		headingRef.current?.focus()
	}, [])
	return { headingRef, descriptionId }
}

interface NoAccountProps {
	email: string
	/** Existing sign-up, with the email prefilled */
	createAccount: ReactNode
	onTryAnotherEmail: () => void
}

/** The code was right, but no account uses this email (401_User_001). Shown only after the user proved the address. */
export function EmailCodeNoAccount({ email, createAccount, onTryAnotherEmail }: Readonly<NoAccountProps>) {
	const { headingRef, descriptionId } = useFocusOnArrival()
	return (
		<div className="space-y-6" data-testid="email-code-no-account">
			<div className="space-y-2 text-center">
				<h2
					ref={headingRef}
					tabIndex={-1}
					aria-describedby={descriptionId}
					className="text-xl font-bold text-primary-text outline-none"
				>
					No account found
				</h2>
				<p id={descriptionId} className="text-secondary-text">
					We couldn't find an account for{' '}
					<span className="break-words font-semibold text-primary-text">{email}</span>.
				</p>
			</div>
			<div className="flex flex-col items-center gap-3">
				{createAccount}
				<Button variant="link" className="-my-2.5 py-2.5" onClick={onTryAnotherEmail}>
					Try a different email
				</Button>
			</div>
		</div>
	)
}
